/**
 * Le numéro de registre national : le lire, et dire s'il est cohérent.
 *
 * ## Pourquoi plus qu'une clé de contrôle
 *
 * La vérification d'origine (`validateBelgianNRN`) ne regardait que les deux
 * derniers chiffres. Elle acceptait « 00000000097 », un 30 février, un numéro
 * d'enfant dans la case du parent, ou le numéro de l'aîné recopié sur la fiche
 * du cadet. Or ce numéro ne sert qu'à une chose : l'attestation fiscale, où
 * un numéro faux vaut moins qu'un numéro absent — l'attestation part au
 * mauvais nom, ou le SPF la rejette, et la famille le découvre en mai.
 *
 * D'où une seule règle, partagée par la fiche du parent et celle de l'enfant :
 * onze chiffres, une clé juste (qui donne le siècle), une date qui existe, et
 * — quand on la connaît par ailleurs — la même date de naissance que celle
 * qui a été saisie. Un numéro refusé ne s'enregistre jamais ; on propose de
 * continuer sans.
 *
 * ## Ce que le numéro contient
 *
 * `AA MM JJ · NNN · CC`. La clé `CC` vaut `97 − (AAMMJJNNN mod 97)` pour une
 * naissance avant 2000, et `97 − (2AAMMJJNNN mod 97)` ensuite : c'est elle qui
 * dit le siècle. Le mois vaut `00` quand la date est inconnue (anciens
 * numéros, personnes nées à l'étranger), et il est augmenté de 20 ou de 40
 * dans un numéro « bis » attribué par la Banque-Carrefour. Le jour vaut `00`
 * quand lui seul est inconnu. Rien ici ne lit ni ne déduit le sexe.
 */

export interface NRNLu {
  /** Les onze chiffres. */
  numero: string;
  annee: number;
  /** 1 à 12, ou 0 si le mois est inconnu. */
  mois: number;
  /** 1 à 31, ou 0 si le jour est inconnu. */
  jour: number;
  /** `AAAA-MM-JJ`, ou `null` si la date n'est pas entièrement connue. */
  dateNaissance: string | null;
  /** Numéro « bis » : le mois porte +20 ou +40. */
  bis: boolean;
}

export type RaisonNRN = 'longueur' | 'cle' | 'date';

const joursDuMois = (annee: number, mois: number) => new Date(Date.UTC(annee, mois, 0)).getUTCDate();

const iso = (annee: number, mois: number, jour: number) =>
  `${String(annee).padStart(4, '0')}-${String(mois).padStart(2, '0')}-${String(jour).padStart(2, '0')}`;

/** Ne garde que les chiffres : « 86.08.15-119.32 » → « 86081511932 ». */
export const chiffresNRN = (brut: string) => (brut ?? '').replace(/\D/g, '');

/** Lit un numéro, ou dit pourquoi il est illisible. */
export function lireNRN(brut: string): { ok: true; lu: NRNLu } | { ok: false; raison: RaisonNRN } {
  const numero = chiffresNRN(brut);
  if (numero.length !== 11) return { ok: false, raison: 'longueur' };

  const corps = parseInt(numero.slice(0, 9), 10);
  const cle = parseInt(numero.slice(9), 10);
  let siecle: 1900 | 2000;
  if (97 - (corps % 97) === cle) siecle = 1900;
  else if (97 - ((2_000_000_000 + corps) % 97) === cle) siecle = 2000;
  else return { ok: false, raison: 'cle' };

  const annee = siecle + parseInt(numero.slice(0, 2), 10);
  const moisBrut = parseInt(numero.slice(2, 4), 10);
  const jour = parseInt(numero.slice(4, 6), 10);

  const bis = moisBrut > 12;
  const mois = moisBrut > 40 ? moisBrut - 40 : moisBrut > 20 ? moisBrut - 20 : moisBrut;
  if (mois > 12) return { ok: false, raison: 'date' };
  // Un mois inconnu n'a pas de jour ; un jour connu demande un mois connu.
  if (mois === 0 && jour !== 0) return { ok: false, raison: 'date' };
  if (mois > 0 && jour > joursDuMois(annee, mois)) return { ok: false, raison: 'date' };

  const dateNaissance = mois > 0 && jour > 0 ? iso(annee, mois, jour) : null;
  return { ok: true, lu: { numero, annee, mois, jour, dateNaissance, bis } };
}

/** « 1986-08-15 » → « 15/08/1986 ». */
export function dateLisible(isoDate: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : isoDate;
}

/** « 2020-03-19 » → « 200319 » : les six premiers chiffres du registre national. */
export function prefixeNRN(dateNaissance: string): string {
  const m = /^\d{2}(\d{2})-(\d{2})-(\d{2})$/.exec((dateNaissance ?? '').trim());
  return m ? `${m[1]}${m[2]}${m[3]}` : '';
}

/** L'âge révolu à une date, à partir d'une date `AAAA-MM-JJ`. */
export function ageA(dateNaissance: string, jour: Date): number {
  const [a, m, j] = dateNaissance.split('-').map(Number);
  let age = jour.getFullYear() - a;
  const anniversairePasse =
    jour.getMonth() + 1 > m || (jour.getMonth() + 1 === m && jour.getDate() >= j);
  if (!anniversairePasse) age -= 1;
  return age;
}

/** L'âge d'un adulte responsable : en deçà, c'est un numéro d'enfant ; au-delà, une faute de frappe. */
export const AGE_MIN_RESPONSABLE = 16;
export const AGE_MAX_RESPONSABLE = 110;

export interface ContexteNRN {
  /** De qui est ce numéro : le responsable du compte, ou un enfant. */
  qui: 'parent' | 'enfant';
  /** La date de naissance saisie par ailleurs (`AAAA-MM-JJ`), à laquelle le numéro doit correspondre. */
  dateNaissance?: string;
  /** Les numéros des autres personnes du compte : le même numéro ne peut pas être porté deux fois. */
  interdits?: string[];
  aujourdhui?: Date;
}

/**
 * Ce qui empêche d'enregistrer ce numéro, en clair ; `null` s'il est cohérent.
 *
 * Un champ vide n'est pas une erreur : le numéro est facultatif partout, c'est
 * l'appelant qui décide d'insister.
 */
export function erreurNRN(brut: string, contexte: ContexteNRN): string | null {
  const lecture = lireNRN(brut);
  if (!lecture.ok) {
    switch (lecture.raison) {
      case 'longueur':
        return 'Le numéro de registre national compte 11 chiffres.';
      case 'cle':
        return 'La clé de contrôle de ce numéro est invalide : un chiffre est sans doute erroné.';
      case 'date':
        return 'La date contenue dans ce numéro n’existe pas.';
    }
  }
  const { lu } = lecture;
  const aujourdhui = contexte.aujourdhui ?? new Date();

  const interdits = (contexte.interdits ?? []).map(chiffresNRN).filter((n) => n.length === 11);
  if (interdits.includes(lu.numero)) {
    return contexte.qui === 'parent'
      ? 'Ce numéro est celui d’un de vos enfants, pas le vôtre.'
      : 'Ce numéro est déjà celui d’une autre personne de votre compte.';
  }

  if (lu.dateNaissance) {
    if (lu.dateNaissance > aujourdhui.toISOString().slice(0, 10)) {
      return 'La date contenue dans ce numéro est dans le futur.';
    }
    if (contexte.dateNaissance && lu.dateNaissance !== contexte.dateNaissance) {
      return contexte.qui === 'parent'
        ? `Ce numéro ne correspond pas à votre date de naissance : il donne le ${dateLisible(lu.dateNaissance)}.`
        : `Ce numéro ne correspond pas à la date de naissance indiquée : il donne le ${dateLisible(lu.dateNaissance)}.`;
    }
    if (contexte.qui === 'parent') {
      const age = ageA(lu.dateNaissance, aujourdhui);
      if (age < AGE_MIN_RESPONSABLE) {
        return `Ce numéro donne un âge de ${age} ans : c’est sans doute celui d’un enfant, pas le vôtre.`;
      }
      if (age > AGE_MAX_RESPONSABLE) {
        return `Ce numéro donne une naissance en ${lu.annee} : vérifiez les six premiers chiffres.`;
      }
    }
  } else if (contexte.qui === 'parent' && aujourdhui.getFullYear() - lu.annee < AGE_MIN_RESPONSABLE) {
    return 'Ce numéro donne une année de naissance trop récente pour être le vôtre.';
  }

  return null;
}
